import { common } from "./common.js";

export const lists = {
  home: {
    hello: "Hallo",
    continue: "Weiterüben",
    dueTitle: "Heute dran",
    /** Shared class goal (PLAN.md §9): a total, never a person. */
    goalTitle: "Klassenziel diese Woche",
    goalCount: (done: string, target: string) => `Zusammen: ${done} von ${target} Antworten`,
    goalHint: "Jede Antwort zählt für die ganze Klasse.",
    goalReached: "Ziel erreicht. Jede weitere Antwort zählt trotzdem.",
    goalAria: (done: string, target: string) => `Klassenziel: ${done} von ${target} Antworten`,
    dueWords: (n: number, lang: string) => `${common.words(n)} ${lang}`,
    streakUnit: (n: number) => (n === 1 ? "Tag" : "Tage"),
    streakAria: (n: number) => `Du hast ${n} Tage in Folge geübt.`,
    emptyContinue: "Dann kannst du hier üben.",
    practice: "Üben",
    myLists: "Meine Listen",
    emptyTitle: "Hier ist noch nichts",
    emptyText: "Tippe die Wörter ab oder füge sie ein.",
    firstList: "Erste Liste anlegen",
    classLists: "Klassenlisten",
    classEmptyTitle: "Noch keine Klassenlisten",
    classEmptyText: "Sobald jemand eine Liste teilt, steht sie hier.",
    newBadge: "neu",
    /** Lists the child marked «Aktuell», at most three (issue #99). */
    current: "Aktuell",
    archiveLink: (n: number) => `Archiv (${n})`,
  },
  /** Archived lists: off Start, still in «heute dran» (issue #99). */
  archive: {
    title: "Archiv",
    hint: "Die Wörter kommen weiter bei «Heute dran».",
    empty: "Hier ist nichts archiviert.",
    review: "Mehrere Listen wiederholen",
  },
  /** One round from several lists of one language (issue #101). */
  review: {
    title: "Wiederholen",
    hint: "Kreuz die Listen an, die du wiederholen willst. Zuerst kommen die Wörter, die noch wackeln.",
    /** Shown on the other language once a list is ticked: the tiles never mix (#49). */
    oneLang: "Eine Runde übt eine Sprache.",
    maxLists: (n: number) => `Höchstens ${n} Listen in einer Runde.`,
    archived: "Archiv",
    start: (n: number) => `Wiederholen (${common.words(n)})`,
    empty: "Noch keine Listen zum Wiederholen.",
  },
  /** Stars and exam readiness (docs/STARS.md §3, docs/COPY.md §3). */
  progress: {
    starsAria: (n: number) => `${n} von 3 Sternen`,
    /** A word that has been asked but carries no star yet (docs/STARS.md §8). */
    startedAria: (n: number) =>
      n === 0 ? "noch nicht geübt" : n === 1 ? "in einer Richtung geübt" : "in beiden Richtungen geübt",
    /** «12 von 15 sitzen für Freitag» — one display, one goal. */
    ready: (ready: number, total: number, when: string) =>
      `${ready} von ${total} sitzen für ${when}`,
    readyAria: (ready: number, total: number, when: string) =>
      `${ready} von ${total} Wörtern sitzen für ${when}`,
    today: "heute",
    tomorrow: "morgen",
    /** 0 = Sonntag, wie JavaScript zählt. */
    weekdays: [
      "Sonntag",
      "Montag",
      "Dienstag",
      "Mittwoch",
      "Donnerstag",
      "Freitag",
      "Samstag",
    ],
  },
  detail: {
    speakAria: (word: string) => `«${word}» vorlesen`,
    forkedFrom: (title: string) => `kopiert von ${title}`,
    practice: "Diese Liste üben",
    edit: "Bearbeiten",
    share: "Mit Klasse teilen",
    unshare: "Nicht mehr teilen",
    copy: common.copy,
    markCurrent: "Als aktuell markieren",
    unmarkCurrent: "Nicht mehr aktuell",
    archive: "Archivieren",
    unarchive: "Zurückholen",
    print: "Zum Ausdrucken",
  },
  /** The sheet for writing out by hand (issue #102). */
  print: {
    back: "← Zur Liste",
    direction: (lang: string) => `Deutsch → ${lang}`,
    /** The PDF font has no «→». */
    directionPdf: (lang: string) => `Deutsch – ${lang}`,
    print: "Drucken",
    shuffle: "Neu mischen",
    /** Not «Seite 2»: a long list needs more than one page before the solution. */
    hint: "Die Lösung steht auf einer eigenen Seite am Schluss. Im Druckfenster kannst du auch «Als PDF sichern» wählen.",
    /** In the iOS home-screen app, «Drucken» opens the share sheet with a PDF. */
    hintShare:
      "Die Lösung steht auf einer eigenen Seite am Schluss. «Drucken» öffnet das Teilen-Menü, dort findest du «Drucken» und «In Dateien sichern».",
    /** iOS ignores window.print() in a web app started from the home screen; seen only where sharing a PDF fails too. */
    noPrintHere:
      "Die App vom Home-Bildschirm kann auf iPhone und iPad nicht drucken. Öffne voci.voytrex.net in Safari, melde dich dort an und tippe auf «Drucken».",
    solution: "Lösung",
    /** Between two accepted answers. Not «/»: «cher / chère» is one answer with both forms. */
    or: "oder",
    footer: "VociFuchs · voci.voytrex.net",
  },
  editor: {
    createTitle: "Neue Liste",
    editTitle: "Liste bearbeiten",
    title: "Titel",
    language: "Sprache",
    languageLocked: "Bleibt so. Für eine andere Sprache leg eine neue Liste an.",
    exam: "Prüfung",
    examAria: "Prüfungstermin",
    clearExam: "Kein Prüfungstermin",
    examHint: "Nur wenn die Liste für einen Test ist.",
    source: "Woher sind die Wörter?",
    words: "Wörter",
    deleteRow: "Zeile löschen",
    deleteRowAria: (i: number) => `Zeile ${i} löschen`,
    deAria: (i: number) => `Deutsch, Zeile ${i}`,
    langAria: (lang: string, i: number) => `${lang}, Zeile ${i}`,
    altHint: "Gibt es mehrere richtige Antworten, kannst du sie einzeln dazuschreiben.",
    addAlt: "+ Noch eine Antwort",
    altAria: (row: number, n: number) => `Weitere richtige Antwort ${n}, Zeile ${row}`,
    dropAltAria: (row: number, n: number) => `Weitere richtige Antwort ${n} löschen, Zeile ${row}`,
    errAltTooLong: (row: number, max: number) =>
      `Zeile ${row} hat zu viele Antworten. Zusammen dürfen es ${max} Zeichen sein.`,
    quickLoad: "Schnell laden",
    scan: "Foto scannen",
    scanHint: (lang: string) =>
      `Ein Foto vom Blatt mit Deutsch und ${lang}. Das Foto wird nur gelesen und nicht gespeichert.`,
    scanning: "Das Foto wird gelesen …",
    /** Measured: 20 s for a worksheet, 45 s for a dense book page; the worker
     *  gives up after 85 s (#82). Without this a child taps again. */
    scanWait: "Das Foto wird gelesen. Das kann bis zu anderthalb Minuten dauern.",
    scanDone: (n: number, unsure: number) =>
      `${common.words(n)} gelesen.` +
      (unsure > 0 ? ` ${unsure === 1 ? "1 Zeile" : `${unsure} Zeilen`} bitte prüfen, sie stehen zuoberst.` : ""),
    scanDropped: "Ein paar Zeilen haben wir weggelassen.",
    scanTruncated: "Mehr Wörter passen nicht in eine Liste.",
    scanFallback: "Du kannst die Wörter hier einfügen.",
    unsureRow: "Bitte prüfen",
    paste: "Wörter einfügen",
    pasteHint:
      "Eine Zeile pro Wort. Geht mit = ; : oder Tab. Zum Beispiel: Katze = cat. Mehrere richtige Antworten trennst du hier mit einem senkrechten Strich: cat | feline",
    recognized: (n: number, skipped?: string) =>
      `${n} Wörter erkannt${skipped ? ` · Zeile ${skipped} übersprungen` : ""}.`,
    replaceConfirm: (label: string) => `Die Liste hat schon ${label}. Hinzufügen oder ersetzen?`,
    append: "Hinzufügen",
    replace: "Ersetzen",
    alreadyThere: (n: number) => (n === 1 ? "1 Wort war schon da." : `${n} Wörter waren schon da.`),
    pasteAdded: (n: number) => `${common.words(n)} dazugekommen.`,
    allThere: "Alles war schon da.",
    repeatedRow: "Steht schon weiter oben.",
    apply: "Übernehmen",
    saveAndShare: "Speichern und mit Klasse teilen",
    deleteList: "Liste löschen",
    errTitle: "Gib der Liste einen Titel.",
    errHalfRow: "Eine Zeile ist nur halb ausgefüllt. Füll sie aus oder lösch sie.",
    errNeedWord: "Schreib mindestens ein Wort auf.",
    errTooMany: (max: number) => `Eine Liste fasst ${max} Wörter. Mach zwei daraus.`,
  },
  delete: {
    title: "Liste löschen?",
    body: (title: string) =>
      `«${title}» verschwindet aus deiner Übersicht. Was du an diesen Wörtern schon gelernt hast, bleibt dir erhalten.`,
    /** Geteilt heisst: sie verschwindet auch bei allen anderen. Das muss dastehen. */
    bodyShared: (title: string) =>
      `«${title}» verschwindet aus deiner Übersicht und aus der Klasse — auch bei allen anderen. Was ihr an diesen Wörtern schon gelernt habt, bleibt euch erhalten.`,
    confirm: "Ja, löschen",
    keep: "Behalten",
  },
} as const;
